// Attribution capture (2026-06-13). Reads UTM params off the current URL
// and persists them in localStorage so they survive route changes and the
// email-verification round-trip. Read at signup time to populate the
// `referrerSource` field sent to the backend, which writes it to
// users.referrer_source for per-channel attribution.

const STORAGE_KEY = "funizzle_referrer";

export type ReferrerData = {
  source?: string | null;
  campaign?: string | null;
  medium?: string | null;
  capturedAt?: string;
};

// Call once on app boot. Parses ?utm_source/?utm_campaign/?utm_medium from
// the current URL and writes them to localStorage. If no UTMs are present
// in the URL, leaves any previously-stored value alone (so a user who
// arrived via LinkedIn last week, browsed away, and came back today still
// has the original source on file).
export function captureAttributionFromUrl(): void {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const source = params.get("utm_source");
    const campaign = params.get("utm_campaign");
    const medium = params.get("utm_medium");
    if (!source && !campaign && !medium) return;
    const existing = loadAttribution();
    const data: ReferrerData = {
      source: source ?? existing?.source ?? null,
      campaign: campaign ?? existing?.campaign ?? null,
      medium: medium ?? existing?.medium ?? null,
      capturedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage may be unavailable in private mode; silently no-op.
  }
}

export function loadAttribution(): ReferrerData | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ReferrerData) : null;
  } catch {
    return null;
  }
}

// Returns a compact string suitable for users.referrer_source. Prefers
// utm_source, falls back to medium, then null. The campaign is appended
// in parens when present so a single column captures both (cheap denorm).
//   linkedin (pk_gamedevs_2026q2)
//   facebook (fz_pk_and_w23)
//   organic
export function buildReferrerSource(): string | null {
  const a = loadAttribution();
  if (!a) return null;
  const base = a.source || a.medium;
  if (!base) return null;
  return a.campaign ? `${base} (${a.campaign})` : base;
}
